import { useEffect, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Skeleton,
	Stack,
	Typography,
	useMediaQuery,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { format, parseISO } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import Label from "src/components/common/Label";
import { TRYOUT_OPEN_SUMMARY_ANALYSIS_EVENT } from "src/components/tryout/SummaryAnalysis";
import { getTryoutMiniProgress } from "src/services/tryout.service";
import { showCategory } from "src/utils/Tryout";

type CategoryScore = {
	id: number;
	name: string;
	score: number;
	passing_grade: number;
	max_score: number;
	is_passed: boolean;
};

type TryAttempt = {
	tries: number;
	total_score: number;
	is_passed: boolean;
	taken_at: string;
	categories: CategoryScore[];
};

export type MiniProgressData = {
	comparable: boolean;
	tries_count: number;
	delta_points: number;
	latest: TryAttempt | null;
	previous: TryAttempt | null;
};

type MiniProgressVariant = "sidebar" | "app";

type MiniProgressProps = {
	tryoutId: string;
	/** sidebar = drawer/web; app = webview tanpa hamburger */
	variant?: MiniProgressVariant;
};

const isAppWebView = () => localStorage.getItem("isMobile") === "true";

const formatTakenAt = (iso: string) => {
	try {
		return format(parseISO(iso), "dd MMM yyyy HH.mm", { locale: idLocale });
	} catch {
		return iso;
	}
};

const isEmptyAttempt = (attempt: Pick<TryAttempt, "total_score">) =>
	attempt.total_score === 0;

const formatSignedPoints = (delta: number) => {
	const sign = delta > 0 ? "+" : "";
	return `${sign}${delta} poin`;
};

const formatPercentDelta = (
	latestScore: number,
	previousScore: number
): string | null => {
	if (previousScore === 0) return null;
	const pct = Math.round(((latestScore - previousScore) / previousScore) * 100);
	const sign = pct > 0 ? "+" : "";
	return `${sign}${pct}%`;
};

const getUserHint = (latest: TryAttempt) => {
	if (isEmptyAttempt(latest)) {
		return "Tries terakhir belum ada jawaban. Kerjakan soal agar progress bisa dibandingkan dengan tries sebelumnya.";
	}
	if (latest.is_passed) {
		return "Skor terakhir sudah mencapai passing grade. Pertahankan dengan review pembahasan.";
	}
	const failedCategories = latest.categories.filter((c) => !c.is_passed);
	if (failedCategories.length > 0) {
		const names = failedCategories.map((c) => c.name).join(", ");
		return `Masih ada kategori yang belum lulus (${names}). Fokus review pembahasan sebelum tryout berikutnya.`;
	}
	return "Lanjutkan review pembahasan untuk meningkatkan skor di tryout berikutnya.";
};

const CategoryScoreDisplay = ({
	category,
}: {
	category: Pick<CategoryScore, "score" | "passing_grade" | "is_passed">;
}) => (
	<Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{ minWidth: "80px" }}>
		<Label color={category.is_passed ? "success" : "error"}>
			<b>{category.score}</b>
		</Label>
		{category.passing_grade > 0 && (
			<Label color="secondary">
				<b>/{category.passing_grade}</b>
			</Label>
		)}
	</Stack>
);

const CategoryPassIcon = ({ isPassed }: { isPassed: boolean }) =>
	isPassed ? (
		<DoneRoundedIcon
			sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }}
			aria-hidden
		/>
	) : (
		<CancelRoundedIcon
			sx={{ fontSize: 16, color: "error.main", flexShrink: 0 }}
			aria-hidden
		/>
	);

const CategoryBreakdown = ({
	categories,
	defaultExpanded = false,
}: {
	categories: CategoryScore[];
	defaultExpanded?: boolean;
}) => {
	if (categories.length === 0) return null;

	const passedCount = categories.filter((c) => c.is_passed).length;

	return (
		<Accordion
			disableGutters
			defaultExpanded={defaultExpanded}
			sx={(theme) => ({
				mb: 1.5,
				bgcolor: theme.colors.alpha.black[5],
				border: `1px solid ${theme.colors.alpha.black[10]}`,
				borderRadius: "12px !important",
				boxShadow: "none",
				"&:before": { display: "none" },
			})}
		>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon aria-hidden />}
				aria-label="Rincian kategori, klik untuk membuka"
				sx={{
					minHeight: 48,
					px: 1.5,
					cursor: "pointer",
					"& .MuiAccordionSummary-content": { my: 1.25 },
				}}
			>
				<Stack spacing={0.25} sx={{ minWidth: 0 }}>
					<Typography
						variant="caption"
						fontWeight={700}
						sx={{ color: "text.primary", lineHeight: 1.3 }}
					>
						Rincian kategori
					</Typography>
					<Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
						{passedCount}/{categories.length} kategori lulus
					</Typography>
				</Stack>
			</AccordionSummary>
			<AccordionDetails sx={{ pt: 0, pb: 1.25, px: 1.25 }}>
				<Stack spacing={1} component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
					{categories.map((category) => (
						<Stack
							key={category.id}
							component="li"
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							spacing={1}
							sx={{ minWidth: 0 }}
							aria-label={`${category.name}: ${category.score}${category.passing_grade > 0 ? ` dari ${category.passing_grade}` : ""}, ${category.is_passed ? "lulus" : "tidak lulus"}`}
						>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
								<CategoryPassIcon isPassed={category.is_passed} />
								<Typography
									variant="caption"
									fontWeight={600}
									sx={{
										color: "text.primary",
										lineHeight: 1.3,
										minWidth: 0,
										overflowWrap: "anywhere",
									}}
								>
									{showCategory(category.name)}
								</Typography>
							</Stack>
							<CategoryScoreDisplay category={category} />
						</Stack>
					))}
				</Stack>
			</AccordionDetails>
		</Accordion>
	);
};

const PassStatus = ({
	attempt,
}: {
	attempt: Pick<TryAttempt, "total_score" | "is_passed">;
}) => {
	const stackSx = {
		minWidth: 0,
		maxWidth: "100%",
	} as const;

	const labelSx = {
		letterSpacing: 0.1,
		lineHeight: 1.3,
		minWidth: 0,
		overflowWrap: "anywhere" as const,
	};

	if (isEmptyAttempt(attempt)) {
		return (
			<Stack
				direction="row"
				alignItems="flex-start"
				spacing={0.5}
				sx={stackSx}
				aria-label="Status: Belum dikerjakan"
			>
				{/* <InfoOutlinedIcon
					sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0, mt: "1px" }}
					aria-hidden
				/> */}
				<Typography
					variant="caption"
					fontWeight={600}
					sx={{
						...labelSx,
						color: "text.secondary",
					}}
				>
					Belum dikerjakan
				</Typography>
			</Stack>
		);
	}

	const { is_passed: isPassed } = attempt;

	return (
		<Stack
			direction="row"
			alignItems="flex-start"
			spacing={0.5}
			sx={stackSx}
			aria-label={isPassed ? "Status: Lulus" : "Status: Tidak lulus"}
		>
			{isPassed ? (
				<ThumbUpAltOutlinedIcon
					sx={{ fontSize: 16, color: "success.main", flexShrink: 0, mt: "1px" }}
					aria-hidden
				/>
			) : (
				<ThumbDownAltOutlinedIcon
					sx={{ fontSize: 16, color: "error.main", flexShrink: 0, mt: "1px" }}
					aria-hidden
				/>
			)}
			<Typography
				variant="caption"
				fontWeight={600}
				sx={{
					...labelSx,
					color: isPassed ? "success.dark" : "error.dark",
				}}
			>
				{isPassed ? "Lulus" : "Tidak lulus"}
			</Typography>
		</Stack>
	);
};

const AttemptColumn = ({
	attempt,
	tone,
}: {
	attempt: TryAttempt;
	tone: "previous" | "latest";
}) => (
	<Box sx={{ flex: 1, minWidth: 0 }}>
		<Typography
			component="p"
			sx={{
				m: 0,
				fontSize: "1.25rem",
				fontWeight: 700,
				lineHeight: 1.15,
				fontVariantNumeric: "tabular-nums",
				color: tone === "previous" ? "primary.main" : "text.primary",
			}}
		>
			{attempt.total_score}
		</Typography>
		<Box sx={{ mt: 0.5, minWidth: 0, maxWidth: "100%" }}>
			<PassStatus attempt={attempt} />
		</Box>
		<Typography
			variant="caption"
			color="text.secondary"
			display="block"
			sx={{ mt: 0.5, lineHeight: 1.35, fontSize: "0.6875rem" }}
		>
			{formatTakenAt(attempt.taken_at)}
		</Typography>
	</Box>
);

const DeltaChip = ({
	deltaPoints,
	percentLabel,
}: {
	deltaPoints: number;
	percentLabel: string | null;
}) => {
	const isUp = deltaPoints > 0;
	const isDown = deltaPoints < 0;
	const Icon = isUp
		? TrendingUpRoundedIcon
		: isDown
			? TrendingDownRoundedIcon
			: TrendingFlatRoundedIcon;
	const label = [
		formatSignedPoints(deltaPoints),
		percentLabel,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<Box
			component="span"
			role="status"
			aria-label={`Perubahan skor ${label}`}
			sx={(theme) => ({
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				px: 1.25,
				py: 0.5,
				borderRadius: "10px",
				bgcolor: isUp
					? theme.colors.success.lighter
					: isDown
						? theme.colors.error.lighter
						: theme.colors.alpha.black[5],
				color: isUp
					? theme.palette.success.dark
					: isDown
						? theme.palette.error.dark
						: theme.palette.text.secondary,
				border: "1px solid",
				borderColor: isUp
					? theme.colors.success.light
					: isDown
						? theme.colors.error.light
						: theme.colors.alpha.black[10],
				boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
			})}
		>
			<Icon sx={{ fontSize: 16 }} aria-hidden />
			<Typography
				component="span"
				sx={{
					fontSize: "0.75rem",
					fontWeight: 700,
					lineHeight: 1.2,
					fontVariantNumeric: "tabular-nums",
					color: "inherit",
				}}
			>
				{label}
			</Typography>
		</Box>
	);
};

const DEFAULT_GATE_MESSAGE =
	"Kerjakan tryout minimal 2 kali untuk melihat progress belajar.";

const MiniProgress = ({ tryoutId, variant = "sidebar" }: MiniProgressProps) => {
	const [data, setData] = useState<MiniProgressData | null>(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);
	const [gateMessage, setGateMessage] = useState<string | null>(null);
	const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
	const isApp = variant === "app";

	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			if (!tryoutId) {
				setLoading(false);
				setFailed(true);
				return;
			}

			setLoading(true);
			setFailed(false);
			setGateMessage(null);
			setData(null);

			try {
				const res = await getTryoutMiniProgress(tryoutId);
				const payload = res?.data?.data as MiniProgressData | undefined;
				if (!cancelled) {
					if (!payload) {
						setFailed(true);
					} else {
						setData(payload);
					}
					setLoading(false);
				}
			} catch (error: unknown) {
				if (!cancelled) {
					const axiosError = error as {
						response?: { status?: number; data?: { message?: string } };
					};
					const status = axiosError?.response?.status;
					const message = axiosError?.response?.data?.message;

					if (status === 422) {
						setGateMessage(
							typeof message === "string" && message.trim()
								? message
								: DEFAULT_GATE_MESSAGE
						);
						setFailed(false);
					} else {
						setFailed(true);
					}
					setLoading(false);
				}
			}
		};

		fetchData();
		return () => {
			cancelled = true;
		};
	}, [tryoutId]);

	const handleLihatEvaluasi = () => {
		window.dispatchEvent(new Event(TRYOUT_OPEN_SUMMARY_ANALYSIS_EVENT));
	};

	if (failed) return null;

	const cardShellSx = {
		position: "relative" as const,
		overflow: "hidden",
		p: isApp ? 2.25 : 1.5,
		borderRadius: isApp ? "16px" : "12px",
		bgcolor: "common.white",
		border: "1px solid",
		borderColor: (theme: { colors: { alpha: { black: Record<number, string> } } }) =>
			theme.colors.alpha.black[10],
		boxShadow: isApp
			? "0 2px 4px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.08)"
			: "0 1px 2px rgba(16, 24, 40, 0.05), 0 4px 12px rgba(16, 24, 40, 0.06)",
		...(isApp
			? {
				backgroundImage: (theme: {
					colors: { primary: { lighter: string }; alpha: { black: Record<number, string> } };
				}) =>
					`linear-gradient(165deg, ${theme.colors.primary.lighter} 0%, #ffffff 42%, #ffffff 100%)`,
			}
			: {}),
		...(reduceMotion
			? {}
			: {
				animation: "miniProgressIn 280ms ease-out",
				"@keyframes miniProgressIn": {
					from: { opacity: 0, transform: "translateY(8px)" },
					to: { opacity: 1, transform: "translateY(0)" },
				},
			}),
	};

	if (loading) {
		return (
			<Box sx={cardShellSx} aria-busy="true" aria-live="polite" aria-label="Memuat progress tryout">
				<Skeleton variant="text" width="70%" height={18} sx={{ mb: 1 }} />
				<Skeleton variant="rectangular" width={64} height={40} sx={{ mb: 1.5, borderRadius: "10px" }} />
				<Skeleton variant="text" width="40%" height={16} sx={{ mb: 1.5 }} />
				<Skeleton variant="rectangular" height={44} sx={{ mb: 1.5, borderRadius: "10px" }} />
				<Skeleton variant="rectangular" height={96} sx={{ mb: 2, borderRadius: "10px" }} />
				<Skeleton variant="rectangular" height={44} sx={{ borderRadius: "10px" }} />
			</Box>
		);
	}

	if (gateMessage) {
		return (
			<Box
				component="section"
				aria-label="Mini progress tryout"
				sx={cardShellSx}
			>
				<Typography
					component="p"
					sx={{
						m: 0,
						mb: 1.5,
						fontSize: "0.75rem",
						fontWeight: 500,
						color: "text.secondary",
						letterSpacing: 0.15,
					}}
				>
					Progress belajar
				</Typography>

				<Stack
					direction="row"
					alignItems="flex-start"
					spacing={1.25}
					sx={(theme) => ({
						p: 1.5,
						mb: 2,
						borderRadius: "10px",
						bgcolor: theme.colors.primary.lighter,
						border: `1px solid ${theme.colors.alpha.black[10]}`,
					})}
					role="status"
				>
					<InfoOutlinedIcon
						sx={{ fontSize: 20, color: "primary.main", mt: 0.15, flexShrink: 0 }}
						aria-hidden
					/>
					<Typography
						variant="body2"
						sx={{
							color: "text.primary",
							lineHeight: 1.5,
							fontSize: "0.8125rem",
							fontWeight: 500,
						}}
					>
						{gateMessage}
					</Typography>
				</Stack>

				<Button
					fullWidth
					variant="contained"
					color="primary"
					size="small"
					onClick={handleLihatEvaluasi}
					startIcon={<AutoAwesomeIcon />}
					aria-label="Analisa menggunakan Brodi"
					sx={{
						minHeight: isApp ? 48 : 44,
						cursor: "pointer",
						transition: "background-color 200ms ease, box-shadow 200ms ease",
					}}
				>
					Analisa menggunakan Brodi
				</Button>
			</Box>
		);
	}

	if (!data) return null;

	const { latest, previous, comparable, delta_points, tries_count } = data;
	const hasLatest = Boolean(latest) && tries_count > 0;
	const showComparison = comparable && Boolean(previous) && Boolean(latest);
	const percentLabel =
		showComparison && previous && latest
			? formatPercentDelta(latest.total_score, previous.total_score)
			: null;
	const showScore = hasLatest && (comparable || tries_count > 1);
	const headerScore = showScore && latest ? String(latest.total_score) : "—";
	const hintText = latest
		? getUserHint(latest)
		: "Belum ada hasil tryout untuk ditampilkan.";

	return (
		<Box
			component="section"
			aria-label="Mini progress tryout"
			sx={cardShellSx}
		>
			{/* Soft UI accent blob — decorative only */}
			<Box
				aria-hidden
				sx={(theme) => ({
					position: "absolute",
					top: -28,
					right: -24,
					width: 100,
					height: 100,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${theme.colors.primary.lighter} 0%, transparent 70%)`,
					pointerEvents: "none",
				})}
			/>

			<Typography
				component="p"
				sx={{
					m: 0,
					mb: 1,
					fontSize: "0.75rem",
					fontWeight: 500,
					color: "text.secondary",
					letterSpacing: 0.15,
				}}
			>
				Hasil tryout terakhirmu
			</Typography>

			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				flexWrap="wrap"
				sx={{ mb: 1, gap: 1 }}
			>
				<Typography
					component="p"
					sx={{
						m: 0,
						fontSize: isApp ? "2.75rem" : "2.25rem",
						fontWeight: 800,
						lineHeight: 1,
						fontVariantNumeric: "tabular-nums",
						color: "primary.main",
						letterSpacing: "-0.02em",
					}}
				>
					{headerScore}
				</Typography>
				{showComparison && (
					<DeltaChip deltaPoints={delta_points} percentLabel={percentLabel} />
				)}
			</Stack>

			{latest && (
				<Box sx={{ mb: isApp ? 1 : 0.5 }}>
					<PassStatus attempt={latest} />
				</Box>
			)}

			{latest?.categories && latest.categories.length > 0 && (
				<CategoryBreakdown
					categories={latest.categories}
					defaultExpanded={isApp}
				/>
			)}

			<Box
				sx={(theme) => ({
					p: isApp ? 1.75 : 1.5,
					my: 2,
					borderRadius: isApp ? "14px" : "10px",
					bgcolor: theme.colors.primary.lighter,
					border: `1px solid ${theme.colors.alpha.black[10]}`,
					boxShadow: `inset 0 1px 2px ${theme.colors.alpha.black[10]}`,
				})}
			>
				<Typography
					component="h3"
					sx={{
						m: 0,
						mb: 1.25,
						fontSize: "0.8125rem",
						fontWeight: 700,
						color: "text.primary",
					}}
				>
					Progress belajar
				</Typography>

				{showComparison && previous && latest ? (
					<Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1.25 }}>
						<AttemptColumn attempt={previous} tone="previous" />
						<Box
							aria-hidden
							sx={(theme) => ({
								mt: 0.25,
								width: 28,
								height: 28,
								borderRadius: "50%",
								display: "grid",
								placeItems: "center",
								flexShrink: 0,
								bgcolor: theme.colors.primary.lighter,
								color: theme.palette.primary.main,
							})}
						>
							<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
						</Box>
						<AttemptColumn attempt={latest} tone="latest" />
					</Stack>
				) : (
					<Typography
						variant="caption"
						color="text.secondary"
						display="block"
						sx={{ mb: 1.25, lineHeight: 1.45 }}
					>
						{hasLatest
							? "Belum ada perbandingan — ini percobaan pertamamu."
							: "Belum ada riwayat tryout."}
					</Typography>
				)}

				<Typography
					variant="caption"
					sx={{
						display: "block",
						color: "text.secondary",
						lineHeight: 1.5,
						fontSize: "0.75rem",
					}}
				>
					{hintText}
				</Typography>
			</Box>

			<Button
				fullWidth
				variant="contained"
				color="primary"
				size="small"
				onClick={handleLihatEvaluasi}
				startIcon={<AutoAwesomeIcon />}
				aria-label="Analisa menggunakan Brodi"
				sx={{
					minHeight: isApp ? 48 : 44,
					cursor: "pointer",
					transition: "background-color 200ms ease, box-shadow 200ms ease",
				}}
			>
				Analisa menggunakan Brodi
			</Button>
		</Box>
	);
};

export { isAppWebView };
export default MiniProgress;
